import { computed, inject } from '@angular/core';
import { signalStore, withState, withComputed, withMethods } from '@ngrx/signals';
import { updateState } from '@angular-architects/ngrx-toolkit';
import { firstValueFrom, type Observable } from 'rxjs';
import type { MessengerNetwork, PrintPartnerKey } from '@lite-workspace/dto-user';
import { IDENTITY_DATA, IDENTITY_NOTIFIER } from './identity.data';
import { EMPTY_ONBOARDING_PROGRESS, mergeOnboardingProgress } from './identity.models';
import type { AuthType, LessonBucket, OnboardingProgress, ProfilePatch, UserProfile } from './identity.models';
import { environment } from '@environment';

const LESSON_BUCKETS: readonly LessonBucket[] = ['viewed', 'favorites'];

/** Корзины, которые переигрываются поверх ответа сервера; штамп показа не переигрывается — он не отметка. */
type ReplayedBuckets = { completed: string[]; dismissed: string[]; viewed: string[]; favorites: string[] };

interface IdentityState {
  profile: UserProfile | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: IdentityState = {
  profile: null,
  loading: false,
  saving: false,
  error: null,
};

export const IdentityStore = signalStore(
  { providedIn: 'root' },
  environment.storeWithDevTools('identity'),
  withState<IdentityState>(initialState),
  withComputed(store => ({
    authType: computed<AuthType>(() => store.profile()?.authType ?? 'otp'),
    hasPassword: computed(() => store.profile()?.hasPassword ?? false),
    contacts: computed(() => store.profile()?.contacts ?? []),
    fullName: computed(() => {
      const p = store.profile();
      const first = p?.firstName ?? '';
      const last = p?.lastName ?? '';
      return (first + ' ' + last).trim() || (p?.email ?? '');
    }),
    partnerKey: computed<PrintPartnerKey | null>(() => store.profile()?.partnerKey ?? null),
  })),
  withMethods(store => {
    const data = inject(IDENTITY_DATA);
    const notifier = inject(IDENTITY_NOTIFIER, { optional: true });
    // Серверная правда содержит запись онбординга не с момента её отправки, а с момента, когда она
    // подтверждена собственным ответом. Поэтому ответ на чтение профиля переигрывается такой
    // записью, если та либо ещё не подтверждена (сервер мог собрать ответ раньше, чем запись до
    // него доехала), либо подтверждена уже после старта этого чтения. Одного счётчика «в полёте» не
    // хватало: ответ записи, обогнавший более старое чтение, снимал счётчик до прихода устаревших
    // данных. Одного порядка тиков тоже не хватает: неподтверждённая запись и подтверждённый
    // серверный сброс дают одинаковый порядок и противоположные вердикты.
    let clock = 0;
    /** flowId → корзина записи и тик подтверждения; `null` — запись отправлена и ещё висит. */
    const onboardingWrites = new Map<string, { bucket: 'completed' | 'dismissed'; confirmedAt: number | null }>();
    // Корзины урока живут отдельно от корзин закрытия: урок бывает и просмотрен, и закрыт, и избранным,
    // а любую отметку можно снять. Поэтому журнал хранит последнее намерение фотографа — поставить или
    // снять — с поколением: ответ на запрос, после которого фотограф передумал, уже не решает судьбу урока.
    // Просмотр и сердечко ходят одним путём, поэтому ключ журналов — `bucket:flowId`.
    type LessonWrite = { bucket: LessonBucket; flowId: string; on: boolean; gen: number; confirmedAt: number | null };
    const lessonKey = (bucket: LessonBucket, flowId: string): string => `${bucket}:${flowId}`;
    /** `bucket:flowId` → последняя отметка, её поколение и тик подтверждения; `null` — ещё висит. */
    const lessonWrites = new Map<string, LessonWrite>();
    let lessonGen = 0;
    /** Запросы по одной корзине урока уходят строго по очереди, чтобы сервер применил их в порядке кликов. */
    const lessonTails = new Map<string, Promise<void>>();
    // Откат провала опирается только на то, что сервер точно знает: последний подтверждённый ответ
    // цепочки запросов урока, а без него — состояние до первого запроса цепочки. Висящая или
    // проваленная отметка точкой отката не бывает: сервер её так и не записал.
    /** `bucket:flowId` → журнал и экран до первого запроса цепочки и последний подтверждённый ответ. */
    const lessonChains = new Map<string, { before?: LessonWrite; shownBefore: boolean; acked?: LessonWrite }>();
    /** Сохранения идут строго по одному; хвост очереди никогда не реджектится. */
    let saveTail: Promise<unknown> = Promise.resolve();
    let pendingSaves = 0;
    /** Тик применения последнего эха сохранения — арбитр против отставших чтений. */
    let lastSaveAppliedAt = 0;

    // Снятую отметку объединение не выбьет, поэтому любой ответ сервера — чтение или запись — проходит
    // здесь: урок уходит из просмотра, если его снятие этот ответ ещё не мог знать.
    const withoutUnknownUnmarks = (progress: OnboardingProgress, startedAt: number): OnboardingProgress => {
      let next = progress;
      for (const bucket of LESSON_BUCKETS) {
        const ids = progress[bucket] ?? [];
        const kept = ids.filter(id => {
          const write = lessonWrites.get(lessonKey(bucket, id));
          return !write || write.on || (write.confirmedAt !== null && write.confirmedAt <= startedAt);
        });
        if (kept.length !== ids.length) next = { ...next, [bucket]: kept };
      }

      return next;
    };

    /** Записи корзин закрытия, которых ответ, начатый на тике `startedAt`, ещё не мог знать. */
    const bucketsUnknownTo = (startedAt: number): ReplayedBuckets => {
      const unknown: ReplayedBuckets = { completed: [], dismissed: [], viewed: [], favorites: [] };
      for (const [flowId, { bucket, confirmedAt }] of onboardingWrites) {
        if (confirmedAt === null || confirmedAt > startedAt) unknown[bucket].push(flowId);
      }

      return unknown;
    };

    // Ответ записи одного флоу сервер собрал до записи другого: тот приходит в прежней корзине, и объединение
    // выбило бы его из новой. Поэтому поверх ответа переигрываются записи корзин, которых он ещё не мог знать.
    const withServerProgress = (local: OnboardingProgress, server: OnboardingProgress, startedAt: number): OnboardingProgress => {
      const unknown = bucketsUnknownTo(startedAt);
      const merged = mergeOnboardingProgress(local, server);
      const replayed =
        unknown.completed.length + unknown.dismissed.length > 0 ? mergeOnboardingProgress(merged, unknown) : merged;
      return withoutUnknownUnmarks(replayed, startedAt);
    };

    /** Переигрывает записи онбординга, которых этот ответ ещё не мог знать. */
    const withWritesUnknownTo = (profile: UserProfile, startedAt: number): UserProfile => {
      const unknown = bucketsUnknownTo(startedAt);

      let unknownUnmark = false;
      for (const { bucket, flowId, on, confirmedAt } of lessonWrites.values()) {
        if (confirmedAt !== null && confirmedAt <= startedAt) continue;
        if (on) unknown[bucket].push(flowId);
        else unknownUnmark = true;
      }

      const nothingUnknown =
        unknown.completed.length + unknown.dismissed.length + unknown.viewed.length + unknown.favorites.length === 0;
      if (nothingUnknown && !unknownUnmark) return profile;

      // Корзины взаимоисключающие, поэтому переигранная запись и добавляет флоу в свою, и выбивает из чужой.
      const merged = mergeOnboardingProgress(profile.onboardingProgress ?? EMPTY_ONBOARDING_PROGRESS, unknown);
      return { ...profile, onboardingProgress: withoutUnknownUnmarks(merged, startedAt) };
    };

    /** Ставит или снимает урок в его корзине на экране. */
    const showLesson = (
      bucket: LessonBucket,
      flowId: string,
      on: boolean,
      response?: { progress: OnboardingProgress; startedAt: number },
    ): void => {
      const current = store.profile();
      if (!current) return;
      const base = response
        ? withServerProgress(current.onboardingProgress, response.progress, response.startedAt)
        : current.onboardingProgress;
      const others = (base[bucket] ?? []).filter(id => id !== flowId);
      updateState(store, `[Identity] Onboarding ${bucket === 'viewed' ? 'Viewed' : 'Favorite'} Changed`, {
        profile: { ...current, onboardingProgress: { ...base, [bucket]: on ? [...others, flowId] : others } },
      });
    };

    const sendLesson = (bucket: LessonBucket, flowId: string, on: boolean): Observable<OnboardingProgress> => {
      if (bucket === 'viewed') return on ? data.markLessonViewed(flowId) : data.unmarkLessonViewed(flowId);
      return on ? data.markLessonFavorite(flowId) : data.unmarkLessonFavorite(flowId);
    };

    // Отметка и её снятие — один путь навстречу друг другу: оптимистично на экране, запрос в очередь
    // этой корзины урока, провал откатывает, только если фотограф с тех пор не передумал.
    const writeLesson = (bucket: LessonBucket, flowId: string, on: boolean): Promise<void> => {
      const shown = store.profile()?.onboardingProgress;
      if (!shown || (shown[bucket] ?? []).includes(flowId) === on) return Promise.resolve();
      const key = lessonKey(bucket, flowId);
      const tail = lessonTails.get(key);
      const chain = (tail && lessonChains.get(key)) || { before: lessonWrites.get(key), shownBefore: !on };
      lessonChains.set(key, chain);
      const gen = ++lessonGen;
      lessonWrites.set(key, { bucket, flowId, on, gen, confirmedAt: null });
      showLesson(bucket, flowId, on);
      const isLatest = (): boolean => lessonWrites.get(key)?.gen === gen;

      const send = (): Promise<void> =>
        new Promise<void>(settle => {
          const startedAt = clock;
          sendLesson(bucket, flowId, on).subscribe({
            next: progress => {
              const confirmedAt = ++clock;
              const holds = on || (progress[bucket] ?? []).includes(flowId);
              chain.acked = { bucket, flowId, on: holds, gen, confirmedAt };
              // Устаревший ответ приносит соседние уроки, но не этот: здесь правда — последний клик.
              const latest = isLatest();
              if (latest) lessonWrites.set(key, { bucket, flowId, on, gen, confirmedAt });
              const shown = latest ? holds : (store.profile()?.onboardingProgress[bucket] ?? []).includes(flowId);
              showLesson(bucket, flowId, shown, { progress, startedAt });
              settle();
            },
            error: () => {
              if (isLatest()) {
                // Откат к тому, что сервер точно знает: к подтверждённому ответу цепочки, а без него — к
                // журналу и экрану до её первого запроса.
                const restored = chain.acked ?? chain.before;
                if (restored) lessonWrites.set(key, restored);
                else lessonWrites.delete(key);
                showLesson(bucket, flowId, chain.acked ? chain.acked.on : chain.shownBefore);
              }

              settle();
            },
          });
        });

      const done = tail ? tail.then(send) : send();
      lessonTails.set(key, done);
      void done.then(() => {
        if (lessonTails.get(key) !== done) return;
        lessonTails.delete(key);
        lessonChains.delete(key);
      });
      return done;
    };

    // Optimistic: the banner hides on click, and a failed call restores it rather than leaving a lie on screen.
    // Возвращённый промис оседает и на успехе, и на ошибке: провал уже отыгран откатом и уведомлением,
    // поэтому вызывающему незачем ловить реджект.
    const writeOnboardingFlow = (
      bucket: 'completed' | 'dismissed',
      flowId: string,
      send: () => Observable<OnboardingProgress>,
      failureMessage?: string,
    ): Promise<void> => {
      const previous = store.profile();
      const already = previous?.onboardingProgress[bucket] ?? [];
      if (!previous || already.includes(flowId)) return Promise.resolve();
      const label = bucket[0].toUpperCase() + bucket.slice(1);
      const put = (progress: OnboardingProgress, ids: string[]): OnboardingProgress => ({ ...progress, [bucket]: ids });

      updateState(store, `[Identity] Onboarding Flow ${label}`, {
        profile: { ...previous, onboardingProgress: put(previous.onboardingProgress, [...already, flowId]) },
      });

      // null — «отправлена, ещё не подтверждена»: до ответа сервер мог отдавать профиль без неё.
      onboardingWrites.set(flowId, { bucket, confirmedAt: null });
      return new Promise<void>(settle => {
        const startedAt = clock;
        send().subscribe({
          // Запись остаётся и после успеха, только с тиком: более старое чтение может прийти
          // позже, и лишь этот тик отличает его устаревшие данные от авторитетного сброса.
          next: onboardingProgress => {
            onboardingWrites.set(flowId, { bucket, confirmedAt: ++clock });
            const current = store.profile();
            if (current) {
              updateState(store, '[Identity] Onboarding Progress Synced', {
                profile: {
                  ...current,
                  onboardingProgress: withServerProgress(current.onboardingProgress, onboardingProgress, startedAt),
                },
              });
            }

            settle();
          },
          error: () => {
            // Откат: записи не было, и переигрывать её в будущих ответах нельзя.
            onboardingWrites.delete(flowId);
            if (failureMessage) notifier?.notifyError(failureMessage);
            const current = store.profile();
            if (current) {
              updateState(store, `[Identity] Onboarding ${label} Failed`, {
                profile: {
                  ...current,
                  onboardingProgress: put(
                    current.onboardingProgress,
                    (current.onboardingProgress[bucket] ?? []).filter(id => id !== flowId),
                  ),
                },
              });
            }

            settle();
          },
        });
      });
    };

    // Single-flight: two account menus mount at once, and the second response lands after a dismissal and undoes it.
    const loadProfile = (): void => {
      if (store.loading()) return;
      const startedAt = ++clock;
      updateState(store, '[Identity] Load Start', { loading: true, error: null });
      data.getProfile().subscribe({
        next: profile => {
          // Ответ чтения, начатого до применения более свежего сохранения, устарел целиком —
          // затирать им подтверждённую запись нельзя.
          if (lastSaveAppliedAt > startedAt) {
            updateState(store, '[Identity] Stale Load Discarded', { loading: false });
            return;
          }

          updateState(store, '[Identity] Profile Loaded', {
            profile: withWritesUnknownTo(profile, startedAt),
            loading: false,
          });
        },
        error: (err: Error) =>
          updateState(store, '[Identity] Profile Error', {
            loading: false,
            error: err?.message ?? 'Ошибка загрузки профиля',
          }),
      });
    };

    return {
      loadProfile,
      /** For surfaces that only need a profile in hand: a loaded one is not read again. */
      ensureProfile(): void {
        if (!store.profile()) loadProfile();
      },
      async saveProfile(patch: ProfilePatch | ((current: UserProfile | null) => ProfilePatch)): Promise<void> {
        pendingSaves += 1;
        updateState(store, '[Identity] Save Start', { saving: true });
        const previous = saveTail;
        const run = (async (): Promise<void> => {
          await previous;
          // Тело собирается только теперь, за очередью: эхо предыдущего сохранения уже применено,
          // иначе снапшот стора унёс бы на сервер довылетные значения соседних полей.
          const startedAt = ++clock;
          const resolved = typeof patch === 'function' ? patch(store.profile()) : patch;
          const profile = await firstValueFrom(data.updateProfile(resolved));
          lastSaveAppliedAt = ++clock;
          updateState(store, '[Identity] Save Done', { profile: withWritesUnknownTo(profile, startedAt) });
        })();
        saveTail = run.catch((): undefined => undefined);
        try {
          await run;
        } finally {
          pendingSaves -= 1;
          if (pendingSaves === 0) updateState(store, '[Identity] Save Settled', { saving: false });
        }
      },
      // `quiet` — для отметок, которые фотографу нечем повторить (окно уже закрыто): провал молчит,
      // окно просто покажется при следующем входе.
      completeOnboardingFlow(flowId: string, options: { quiet?: boolean } = {}): void {
        void writeOnboardingFlow(
          'completed',
          flowId,
          () => data.completeOnboardingFlow(flowId),
          options.quiet ? undefined : 'Не удалось сохранить подсказку — попробуйте ещё раз',
        );
      },
      dismissOnboardingFlow(flowId: string): void {
        void writeOnboardingFlow(
          'dismissed',
          flowId,
          () => data.dismissOnboardingFlow(flowId),
          'Не удалось скрыть подсказку — попробуйте ещё раз',
        );
      },
      // Отметка просмотра — бухгалтерия, а не действие фотографа: она не выбивает урок из других
      // корзин и молчит при провале, потому что сообщать тут не о чем.
      markLessonViewed(flowId: string): Promise<void> {
        return writeLesson('viewed', flowId, true);
      },
      // Снятие отметки — тот же путь навстречу: урок уходит из корзины сразу, провал возвращает его
      // на место, а журнал не даёт более старым чтениям вернуть снятую отметку.
      unmarkLessonViewed(flowId: string): Promise<void> {
        return writeLesson('viewed', flowId, false);
      },
      // Сердечко — та же бухгалтерия в своей корзине: молча, без выбивания урока из просмотра.
      markLessonFavorite(flowId: string): Promise<void> {
        return writeLesson('favorites', flowId, true);
      },
      unmarkLessonFavorite(flowId: string): Promise<void> {
        return writeLesson('favorites', flowId, false);
      },
      async setAuthType(authType: AuthType): Promise<void> {
        await firstValueFrom(data.setPassword({ authType }));
        const p = store.profile();
        if (p) updateState(store, '[Identity] Auth Type Set', { profile: { ...p, authType } });
      },
      /** The support channel link with a fresh one-time code; the profile learns the result on its next read. */
      connectMessenger(network: MessengerNetwork): Promise<string> {
        return firstValueFrom(data.connectMessenger(network));
      },
      async disconnectMessenger(network: MessengerNetwork): Promise<void> {
        await firstValueFrom(data.disconnectMessenger(network));
        const p = store.profile();
        if (p) {
          updateState(store, '[Identity] Messenger Disconnected', {
            profile: { ...p, messengers: { ...p.messengers, [network]: null } },
          });
        }
      },
      async setPassword(password: string, authType?: AuthType): Promise<void> {
        await firstValueFrom(data.setPassword({ password, ...(authType ? { authType } : {}) }));
        const p = store.profile();
        if (p) {
          updateState(store, '[Identity] Password Set', {
            profile: { ...p, hasPassword: true, ...(authType ? { authType } : {}) },
          });
        }
      },
    };
  }),
  withMethods(store => {
    // While clicks are queued the screen keeps the last click, not the echo of an earlier one; once the
    // queue drains it shows what the server confirmed last, and a failure reads the server back.
    let confirmed: boolean | undefined;
    let wanted: boolean | undefined;
    let pending = 0;
    let failed = false;
    const show = (value: boolean | undefined): void => {
      const p = store.profile();
      if (p) updateState(store, '[Identity] Marketing Subscription Shown', { profile: { ...p, marketingUnsubscribed: value } });
    };

    return {
      async setMarketingUnsubscribed(marketingUnsubscribed: boolean): Promise<boolean> {
        const profile = store.profile();
        if (!profile) return false;
        if (pending === 0) confirmed = profile.marketingUnsubscribed;
        pending += 1;
        wanted = marketingUnsubscribed;
        show(marketingUnsubscribed);
        try {
          await store.saveProfile({ marketingUnsubscribed });
          confirmed = marketingUnsubscribed;
          return true;
        } catch {
          failed = true;
          return false;
        } finally {
          pending -= 1;
          show(pending > 0 ? wanted : confirmed);
          if (pending === 0 && failed) {
            failed = false;
            store.loadProfile();
          }
        }
      },
    };
  }),
);
